import type { BuildingGeomParentType } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import { TAG_POSITION_BOTTOM, type BuildingGeomParent } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import { isBuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import type { GeneratedRoof, GeneratedRoofSoffit } from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { RoofFace } from "building-types/roofs/roofFace";
import {
  type RoofPerimeterRail,
  RoofPerimeterRailType,
  RoofRailCornerType,
} from "building-types/roofs/roofPerimeterTypes";
import type { SoffitRail } from "building-types/roofs/roofTrimTypes";
import { EaveCutType, EaveType } from "building-types/settings/settingsTypesEnum";
import type { FaceArrayViewEntity } from "building-types/views/viewEntity";
import { ViewEntityType } from "building-types/views/viewEntityType";
import { FASCIA_THICKNESS } from "common/constants/roofTrimConstants";
import type { Table } from "common/data/table";
import uid from "common/utils/uid";
import { isAngledEave } from "doc/fasciaTopTrimSizeToValue";
import { supportsEaveCutType } from "doc/settingsTools";
import { MULTIGABLE_BSP_EPSILON } from "doc/utils/roofTools";
import { faceArrayViewEntityNew, baseDescriptorNew } from "doc/utils/viewEntityTools";
import { faceByFourPoints, faceByNPoints, faceNew, faceOrNullReverse, tagFace, tagFaces, type Face } from "geom/brep";
import {
  copyBSP,
  addOutsidePolygonsToBSP,
  buildPolygonsFromFaces,
  buildEdgePolygon,
  addConvexPlanesToBSPLeaf,
  isValidNodeBSP,
} from "geom/bsp";
import { clipFacesWithPlanes, clipFaces, clippingNodeNew } from "geom/bspClipping";
import type { MaxBSPDepthErrorHandler, NodeBSP, PolygonBSP } from "geom/bspTypes";
import { DIST_EPSILON, ANGLE_EPSILON } from "geom/core";
import {
  planeCopy,
  planeCopyReversed,
  planeDistanceTo,
  planeEval,
  planeIsCoplanar,
  planeMoveAlongNormal,
  planeNew,
  planeNormal,
  planeProject,
  planeProject3,
  planeReProjectPolyLine2,
  PlaneSide,
  polyLine2OnPlane,
  vec3PlaneSide,
} from "geom/plane";
import { planeBSPFromPlane } from "geom/planeBSP";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2EdgeNormal,
  polyLine2Start,
  polyLine2EdgeCount,
  polyLine2End,
  polyLine2Edge,
  polyLine2Close,
  polyLine2SetCW,
} from "geom/polyLine2";
import { type PolyLine2, polyLine2New } from "geom/polyLine2Types";
import {
  polyLine3EdgeSlopeInZ,
  polyLine3EdgeCount,
  polyLine3StartPoint,
  polyLine3EndPoint,
  polyLine3EdgeEnd,
  polyLine3EdgeStart,
  type PolyLine3,
  polyLine3EdgeTangent,
} from "geom/polyLine3";
import type { Vec2 } from "geom/vec2";
import { vec3Add, vec3Sub, vec3Normalize, vec3AngleBetween, vec3Negate, vec3Cross } from "geom/vec3";
import type { RBush3D } from "rbush-3d";
import { getRoofPerimeterRailStartNormal, getRoofPerimeterRailEndNormal, isRoofRailCorner } from "./roofPerimeterRails";
import { getRoofTrimCornerInfo, buildSoffitMiterPlane } from "./roofTrimRails";
import { buildTrimPlaneGroups, buildEaveReturnPlaneGroups, buildRoofTopExtension } from "./roofTrimsTools";

const SOFFIT_Z_FIGHTING_EPSILON = 9e-4; // slightly smaller than MULTIGABLE_BSP_EPSILON
const SOFFIT_FACE_CONDITION_TAG = { type: "CONDITION", condition: "SOFFIT_FACE" };

export function makeSoffit(from: Partial<GeneratedRoofSoffit> & FaceArrayViewEntity): GeneratedRoofSoffit {
  return {
    docEntityHandle: undefined,
    id: from.entityId ?? uid(),
    eaveType: EaveType.BOXED_FULL_RETURN,
    ...from,
    type: "ROOF_SOFFIT",
    entityType: "ROOF_SOFFIT",
  };
}

export function buildSoffitFromRails(
  subRoof: GeneratedRoof,
  soffitRails: SoffitRail[],
  soffitRTree: RBush3D,
  soffits: GeneratedRoofSoffit[],
  maxDepthErrorHandle: MaxBSPDepthErrorHandler,
  extensionRoofTopFaces: RoofFace[] = [],
): GeneratedRoofSoffit[] {
  const isValidInsideRailForMiterPlane = (roofPerimeterRail?: RoofPerimeterRail): boolean => {
    if (!roofPerimeterRail) {
      return false;
    }

    const { type, eaveType, eaveCutType } = roofPerimeterRail;

    return type === RoofPerimeterRailType.EAVE && eaveCutType !== EaveCutType.SQUARED && !isAngledEave[eaveType];
  };

  const isValidInsideCornerForMiterPlane = (
    cornerType: RoofRailCornerType,
    roofPerimeterRail: RoofPerimeterRail,
    neighborRoofPerimeterRail: RoofPerimeterRail | undefined,
  ): boolean => {
    return (
      cornerType === RoofRailCornerType.INSIDE &&
      isValidInsideRailForMiterPlane(roofPerimeterRail) &&
      isValidInsideRailForMiterPlane(neighborRoofPerimeterRail)
    );
  };

  for (const soffitRail of soffitRails) {
    const { roofPerimeterRail } = soffitRail;
    const {
      type,
      rail,
      baseEdge,
      overhang,
      exteriorWallOffsetWithFinish,
      prevPerimeterRail,
      nextPerimeterRail,
      eaveCutType,
    } = roofPerimeterRail;

    const startEaveCutType = prevPerimeterRail?.eaveCutType ?? eaveCutType;
    const endEaveCutType = nextPerimeterRail?.eaveCutType ?? eaveCutType;

    if (overhang - exteriorWallOffsetWithFinish < DIST_EPSILON) {
      continue;
    }

    if (!rail) {
      continue;
    }

    // get the rail slopes
    const railStartSlope = Math.abs(polyLine3EdgeSlopeInZ(rail, 0));
    const railEndSlope = Math.abs(polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1));
    const startNormal = getRoofPerimeterRailStartNormal(roofPerimeterRail);
    const endNormal = getRoofPerimeterRailEndNormal(roofPerimeterRail);

    // corner types
    const {
      type: startCornerType,
      angle: startCornerAngle,
      neighborRail: prevRail,
      otherPerimeterRail: prevOtherPerimeterRail,
    } = getRoofTrimCornerInfo(soffitRail, soffitRTree, false, true);

    const {
      type: endCornerType,
      angle: endCornerAngle,
      neighborRail: nextRail,
      otherPerimeterRail: nextOtherPerimeterRail,
    } = getRoofTrimCornerInfo(soffitRail, soffitRTree, true, true);

    // build miter planes
    let startMiterPlane: Plane | null = null;
    let endMiterPlane: Plane | null = null;

    if (isRoofRailCorner(startCornerType) && prevPerimeterRail) {
      startMiterPlane = buildSoffitMiterPlane(startCornerType, soffitRail, false, prevPerimeterRail);
    }

    if (isRoofRailCorner(endCornerType) && nextPerimeterRail) {
      endMiterPlane = buildSoffitMiterPlane(endCornerType, soffitRail, true, nextPerimeterRail);
    }

    // prepare clip planes
    let startClipPlane: Plane | undefined = undefined;
    let endClipPlane: Plane | undefined = undefined;

    if (isRakeRakeCorner(railStartSlope, prevRail, false) && startCornerType === RoofRailCornerType.OUTSIDE) {
      startClipPlane = startMiterPlane ?? undefined;
    } else if (isRakeEaveCorner(railStartSlope, prevRail, false) && startCornerType === RoofRailCornerType.OUTSIDE) {
      if (startCornerAngle >= Math.PI * 0.5 - ANGLE_EPSILON) {
        const [uX, uY] = polyLine2EdgeNormal(baseEdge, 0);

        if (railStartSlope < DIST_EPSILON) {
          const { exteriorWallOffsetWithFinish = 0 } = prevOtherPerimeterRail || {};
          const [x, y] = polyLine2Start(baseEdge);

          // using some epsilon to avoid z-fighting issues
          startClipPlane = planeMoveAlongNormal(
            planeNew(x, y, 0, uX, uY, 0, 0, 0, 1),
            exteriorWallOffsetWithFinish - SOFFIT_Z_FIGHTING_EPSILON,
          );
        } else {
          const [x, y, z] = polyLine3StartPoint(rail);
          const [vX, vY, vZ] = startEaveCutType === EaveCutType.SQUARED ? startNormal : [0, 0, 1];

          startClipPlane = planeMoveAlongNormal(planeNew(x, y, z, uX, uY, 0, vX, vY, vZ), -FASCIA_THICKNESS);
        }
      } else {
        startClipPlane = startMiterPlane ?? undefined;
      }
    } else if (
      type !== RoofPerimeterRailType.RAKE &&
      startCornerType === RoofRailCornerType.OUTSIDE &&
      startCornerAngle <= Math.PI * 0.5 + ANGLE_EPSILON
    ) {
      startClipPlane = startMiterPlane ?? undefined;
    } else if (isValidInsideCornerForMiterPlane(startCornerType, roofPerimeterRail, prevPerimeterRail)) {
      startClipPlane = startMiterPlane ?? undefined;
    }

    if (isRakeRakeCorner(railEndSlope, nextRail, true) && endCornerType === RoofRailCornerType.OUTSIDE) {
      endClipPlane = endMiterPlane ?? undefined;
    } else if (isRakeEaveCorner(railEndSlope, nextRail, true) && endCornerType === RoofRailCornerType.OUTSIDE) {
      if (endCornerAngle >= Math.PI * 0.5 - ANGLE_EPSILON) {
        const [uX, uY] = polyLine2EdgeNormal(baseEdge, polyLine2EdgeCount(baseEdge) - 1, true);

        if (railEndSlope < DIST_EPSILON) {
          const { exteriorWallOffsetWithFinish = 0 } = nextOtherPerimeterRail || {};
          const [x, y] = polyLine2End(baseEdge);

          // using some epsilon to avoid z-fighting issues
          endClipPlane = planeMoveAlongNormal(
            planeNew(x, y, 0, uX, uY, 0, 0, 0, 1),
            exteriorWallOffsetWithFinish - SOFFIT_Z_FIGHTING_EPSILON,
          );
        } else {
          const [x, y, z] = polyLine3EndPoint(rail);
          const [vX, vY, vZ] = endEaveCutType === EaveCutType.SQUARED ? endNormal : [0, 0, 1];

          endClipPlane = planeMoveAlongNormal(planeNew(x, y, z, uX, uY, 0, vX, vY, vZ), -FASCIA_THICKNESS);
        }
      } else {
        endClipPlane = endMiterPlane ?? undefined;
      }
    } else if (
      type !== RoofPerimeterRailType.RAKE &&
      endCornerType === RoofRailCornerType.OUTSIDE &&
      endCornerAngle <= Math.PI * 0.5 + ANGLE_EPSILON
    ) {
      endClipPlane = endMiterPlane ?? undefined;
    } else if (isValidInsideCornerForMiterPlane(endCornerType, roofPerimeterRail, nextPerimeterRail)) {
      endClipPlane = endMiterPlane ?? undefined;
    }

    // build the soffit faces
    buildSoffitFaces(
      soffitRail,
      startCornerType,
      endCornerType,
      soffitRTree,
      maxDepthErrorHandle,
      startClipPlane,
      endClipPlane,
      soffits,
    );

    // build roof top extension
    buildRoofTopExtension({
      subRoof,
      soffitRail,
      isEnd: false,
      extensionRoofTopFaces,
    });

    buildRoofTopExtension({
      subRoof,
      soffitRail,
      isEnd: true,
      extensionRoofTopFaces,
    });
  }

  return soffits;
}

export function buildSoffitDiscardBSP(
  clipContext: MultiGableRoofClipContext,
  subRoofs: Table<GeneratedRoof>,
): NodeBSP | null {
  let soffitDiscardBSP: NodeBSP | null = copyBSP(clipContext.discardBSP);

  const hasSubRoofSquaredCut = (subRoof: GeneratedRoof): boolean => {
    const { roofSettings } = subRoof;
    let { eaveCutType = EaveCutType.PLUMB } = roofSettings;
    eaveCutType = supportsEaveCutType[roofSettings.eaveType] ? eaveCutType : EaveCutType.PLUMB;

    return eaveCutType === EaveCutType.SQUARED;
  };

  const addPolygons = (polygons: PolygonBSP[]) => {
    soffitDiscardBSP = addOutsidePolygonsToBSP({
      node: soffitDiscardBSP,
      coplanarsToFront: true,
      polygons,
      epsilon: MULTIGABLE_BSP_EPSILON,
    });
  };

  const addFace = (face: Face, hasSquaredCut: boolean) => {
    const { loops, plane } = face;
    const [nX, nY, nZ] = hasSquaredCut ? planeNormal(plane) : [0, 0, 1];
    const perimeter = polyLine2OnPlane(plane, loops[0]);

    // top polygons
    const polygons = buildPolygonsFromFaces({
      faces: [face],
    });

    // edge polygons
    for (let i = 0, l = polyLine3EdgeCount(perimeter); i < l; i++) {
      const [x0, y0, z0] = polyLine3EdgeStart(perimeter, i);
      const [x1, y1, z1] = polyLine3EdgeEnd(perimeter, i);

      const edgePolygon = buildEdgePolygon(x0, y0, z0, x1, y1, z1, -nX, -nY, -nZ);

      if (edgePolygon) {
        polygons.push(edgePolygon);
      }
    }

    // add polygons to the BSP
    addPolygons(polygons);
  };

  for (const subRoofId in subRoofs) {
    const { roofTopFaces, trimsRoofMat } = subRoofs[subRoofId];
    const hasSquaredCut = hasSubRoofSquaredCut(subRoofs[subRoofId]);

    for (const { face } of roofTopFaces) {
      addFace(face, hasSquaredCut);
    }

    for (const { faces } of trimsRoofMat) {
      for (const face of faces) {
        addFace(face, hasSquaredCut);
      }
    }
  }

  return soffitDiscardBSP;
}

function buildSoffitFaces(
  soffitRail: SoffitRail,
  startCornerType: RoofRailCornerType,
  endCornerType: RoofRailCornerType,
  soffitRTree: RBush3D,
  maxDepthErrorHandle: MaxBSPDepthErrorHandler,
  startClipPlane?: Plane,
  endClipPlane?: Plane,
  soffits: GeneratedRoofSoffit[] = [],
): GeneratedRoofSoffit[] {
  const {
    soffitHandle,
    roofPerimeterRail,
    innerPlane,
    midPlane,
    outerPlane,
    fasciaTopRail,
    outerTopRail,
    outerBottomRail,
    midBottomRail,
    innerTopRail,
    innerBottomRail,
    soffitSize,
  } = soffitRail;

  const { rail, prevPerimeterRail, nextPerimeterRail, soffitMaterial, eaveType } = roofPerimeterRail;

  if (!rail) {
    return soffits;
  }

  let prevSoffitSize = soffitSize;
  let nextSoffitSize = soffitSize;

  if (prevPerimeterRail) {
    prevSoffitSize = prevPerimeterRail.fasciaBoardSize - prevPerimeterRail.soffitOffset;
  }

  if (nextPerimeterRail) {
    nextSoffitSize = nextPerimeterRail.fasciaBoardSize - nextPerimeterRail.soffitOffset;
  }

  // get the rail slopes
  const signedStartSlope = polyLine3EdgeSlopeInZ(rail, 0);
  const signedEndSlope = -polyLine3EdgeSlopeInZ(rail, polyLine3EdgeCount(rail) - 1);

  if (!(fasciaTopRail.length === outerBottomRail.length)) {
    console.assert(false);
  }
  if (!(fasciaTopRail.length === outerTopRail.length)) {
    console.assert(false);
  }
  if (!(fasciaTopRail.length === innerBottomRail.length)) {
    console.assert(false);
  }
  if (!(fasciaTopRail.length === innerTopRail.length)) {
    console.assert(false);
  }
  if (!(fasciaTopRail.length === midBottomRail.length)) {
    console.assert(false);
  }

  const outerBottomRail3 = polyLine2OnPlane(outerPlane, outerBottomRail);
  const outerTopRail3 = polyLine2OnPlane(outerPlane, outerTopRail);
  const innerTopRail3 = polyLine2OnPlane(innerPlane, innerTopRail);
  const innerBottomRail3 = buildSoffitInnerBottomRail(innerPlane, outerPlane, innerBottomRail, outerBottomRail3);

  const rails3: PolyLine3[] = [outerBottomRail3, outerTopRail3, innerTopRail3, innerBottomRail3];

  const hasValidMidRail = !planeIsCoplanar(midPlane, outerPlane);
  if (hasValidMidRail) {
    rails3.push(polyLine2OnPlane(midPlane, midBottomRail));
  }

  const faces: Face[] = [];

  const eaveReturnPlaneGroups = buildEaveReturnPlaneGroups(soffitRail, soffitRTree);

  // build the faces for each segment
  const edgeCount = polyLine2EdgeCount(outerBottomRail);
  const railCount = rails3.length;
  for (let i = 0; i < edgeCount; i++) {
    let segmentFaces: Face[] = [];
    let capFaces: Face[] = [];

    const isStartEdge = i === 0;
    const isEndEdge = i === edgeCount - 1;

    const startCapPoints: number[] = [];
    const endCapPoints: number[] = [];
    for (let j = 0; j < railCount; j++) {
      const startRail = rails3[j];
      const endRail = rails3[(j + 1) % railCount];

      const [sx0, sy0, sz0] = polyLine3EdgeStart(startRail, i);
      const [ex0, ey0, ez0] = polyLine3EdgeEnd(startRail, i);

      const [sx1, sy1, sz1] = polyLine3EdgeStart(endRail, i);
      const [ex1, ey1, ez1] = polyLine3EdgeEnd(endRail, i);

      const face = faceByFourPoints(sx0, sy0, sz0, ex0, ey0, ez0, ex1, ey1, ez1, sx1, sy1, sz1);

      if (!face) {
        continue;
      }

      if (j === 3) {
        tagFace(face, TAG_POSITION_BOTTOM);
      }

      segmentFaces.push(face);

      startCapPoints.push(sx0, sy0, sz0);
      endCapPoints.push(ex0, ey0, ez0);
    }

    if (isStartEdge) {
      const face = faceByNPoints(startCapPoints);
      if (face) {
        segmentFaces.push(face);
      }
    }

    if (isEndEdge) {
      const face = faceByNPoints(endCapPoints);
      if (face) {
        const f = faceOrNullReverse(face);
        if (f) {
          segmentFaces.push(f);
        }
      }
    }

    if (isStartEdge || isEndEdge) {
      // build cap faces
      if (isStartEdge) {
        buildSoffitCapFaces(soffitRail, startCornerType, false, startClipPlane, capFaces);
      }

      if (isEndEdge) {
        buildSoffitCapFaces(soffitRail, endCornerType, true, endClipPlane, capFaces);
      }

      // clipping
      const planeGroups = buildTrimPlaneGroups(
        polyLine2Edge(fasciaTopRail, i),
        outerPlane,
        prevSoffitSize,
        nextSoffitSize,
        signedStartSlope,
        signedEndSlope,
      );

      for (const planeGroup of planeGroups) {
        segmentFaces = clipFacesWithPlanes({
          faces: segmentFaces,
          planes: planeGroup.map((plane) => planeCopyReversed(plane)),
          addCapFaces: true,
          maxDepthErrorHandle,
        });

        capFaces = clipFacesWithPlanes({
          faces: capFaces,
          planes: planeGroup.map((plane) => planeCopyReversed(plane)),
          maxDepthErrorHandle,
        });
      }

      if (eaveReturnPlaneGroups.length > 0) {
        const bsps = eaveReturnPlaneGroups
          .map((planes) => addConvexPlanesToBSPLeaf(planes.map((x) => planeBSPFromPlane(x))))
          .filter(isValidNodeBSP);

        segmentFaces = clipFaces({
          faces: segmentFaces,
          options: {
            clippingNodes: bsps.map((bsp) => clippingNodeNew(bsp)),
          },
        });
      }

      const clipPlanes: Plane[] = [];
      if (startClipPlane) {
        clipPlanes.push(startClipPlane);
      }

      if (endClipPlane) {
        clipPlanes.push(endClipPlane);
      }

      segmentFaces = clipFacesWithPlanes({
        faces: segmentFaces,
        planes: clipPlanes,
        addCapFaces: true,
        maxDepthErrorHandle,
      });

      capFaces = clipFacesWithPlanes({
        faces: capFaces,
        planes: clipPlanes,
        maxDepthErrorHandle,
      });
    }

    faces.push(...segmentFaces);
    faces.push(...capFaces);
  }

  let parentType: BuildingGeomParentType | undefined;
  let parentId: string | undefined;
  if (soffitHandle.type === "GENERATED_ENTITY") {
    if (isBuildingEntityHandle(soffitHandle.entity)) {
      parentType = soffitHandle.entity.entityType;
      parentId = soffitHandle.entity.id;
    }
  }

  if (!parentType || !parentId) {
    return soffits;
  }

  const soffitParentTag: BuildingGeomParent = {
    type: "PARENT",
    parentType,
    parentId,
  };

  // add the result
  const generatedSoffit = makeSoffit({
    ...faceArrayViewEntityNew(
      tagFaces(faces, [SOFFIT_FACE_CONDITION_TAG, soffitParentTag]),
      baseDescriptorNew(ViewEntityType.EAVE_SOFFIT, parentId),
      soffitMaterial,
      undefined,
      undefined,
      parentId,
    ),
    eaveType,
  });

  soffits.push(generatedSoffit);

  return soffits;
}

function buildSoffitCapFaces(
  soffitRail: SoffitRail,
  cornerType: RoofRailCornerType,
  isEnd: boolean,
  clipPlane?: Plane,
  faces: Face[] = [],
): Face[] {
  const {
    rail,
    roofPerimeterRail,
    innerPlane,
    midPlane,
    outerPlane,
    outerTopRail,
    outerBottomRail,
    midBottomRail,
    innerBottomRail,
  } = soffitRail;

  const { soffitOffset, eaveCutType } = roofPerimeterRail;

  // recreate the inner top rail for avoiding adding 2 times the vertical offset
  const innerTopRail = planeReProjectPolyLine2(outerTopRail, outerPlane, innerPlane);

  // helper function for convertice the vertices to the cap face plane
  const projection = (rail2: PolyLine2, railPlane: Plane, projectionPlane: Plane, offsetV: number = 0): Vec2 => {
    const rail3 = polyLine2OnPlane(railPlane, rail2);
    const [x, y, z] = isEnd ? polyLine3EndPoint(rail3) : polyLine3StartPoint(rail3);

    const [u, v] = planeProject(projectionPlane, x, y, z);

    return [u, v + offsetV];
  };

  // helper function for adding a cap face
  const addSoffitCapFace = () => {
    const normal = isEnd
      ? getRoofPerimeterRailEndNormal(roofPerimeterRail)
      : getRoofPerimeterRailStartNormal(roofPerimeterRail);

    // project the normal into the inner plane
    let [prjNormalX, prjNormalY, prjNormalZ] = vec3Add(
      normal[0],
      normal[1],
      normal[2],
      innerPlane.originX,
      innerPlane.originY,
      innerPlane.originZ,
    );
    [prjNormalX, prjNormalY, prjNormalZ] = planeProject3(innerPlane, prjNormalX, prjNormalY, prjNormalZ);
    [prjNormalX, prjNormalY, prjNormalZ] = vec3Sub(
      prjNormalX,
      prjNormalY,
      prjNormalZ,
      innerPlane.originX,
      innerPlane.originY,
      innerPlane.originZ,
    );
    [prjNormalX, prjNormalY, prjNormalZ] = vec3Normalize(prjNormalX, prjNormalY, prjNormalZ);

    // calculate the vertical offset for the inner top rail
    const [refX1, refY1, refZ1] = planeEval(outerPlane, 0, -soffitOffset);
    const dist1 = planeDistanceTo(innerPlane, refX1, refY1, refZ1);
    const innerTopOffsetY =
      Math.tan(vec3AngleBetween(normal[0], normal[1], normal[2], prjNormalX, prjNormalY, prjNormalZ)) * dist1;

    // build the cap face plane
    const [originX, originY, originZ] = isEnd ? polyLine3EndPoint(rail) : polyLine3StartPoint(rail);

    let [tX, tY, tZ] = polyLine3EdgeTangent(rail, isEnd ? polyLine3EdgeCount(rail) - 1 : 0, isEnd);
    if (eaveCutType === EaveCutType.PLUMB) {
      [tX, tY, tZ] = vec3Normalize(tX, tY, 0);
    }

    const [nX, nY, nZ] = planeNormal(innerPlane);
    const [uX, uY, uZ] = isEnd ? vec3Negate(nX, nY, nZ) : [nX, nY, nZ];
    const [vX, vY, vZ] = vec3Cross(uX, uY, uZ, tX, tY, tZ);

    const capPlane = planeNew(originX, originY, originZ, uX, uY, uZ, vX, vY, vZ);

    // move the cap plane back if we have an outside corner
    if (cornerType === RoofRailCornerType.OUTSIDE) {
      planeMoveAlongNormal(capPlane, -FASCIA_THICKNESS);
    }

    // generate the cap face loop
    const loopPoints: number[] = [];
    loopPoints.push(...projection(outerBottomRail, outerPlane, capPlane));
    loopPoints.push(...projection(outerTopRail, outerPlane, capPlane));
    loopPoints.push(...projection(innerTopRail, innerPlane, capPlane, innerTopOffsetY));
    loopPoints.push(...projection(innerBottomRail, innerPlane, capPlane));

    const hasValidMidRail = !planeIsCoplanar(midPlane, outerPlane);
    if (hasValidMidRail) {
      loopPoints.push(...projection(midBottomRail, midPlane, capPlane));
    }

    const capLoop = polyLine2SetCW(polyLine2Close(polyLine2New(loopPoints)));

    // add the cap face
    const face = faceNew([capLoop], capPlane);

    faces.push(face);
  };

  if (!clipPlane) {
    addSoffitCapFace();
  }

  return faces;
}

export function buildSoffitInnerBottomRail(
  innerPlane: Plane,
  outerPlane: Plane,
  innerBottomRail: PolyLine2,
  outerBottomRail3: PolyLine3,
): PolyLine3 {
  let isOuterBottonInside = false;
  const [nX, nY, nZ] = planeNormal(innerPlane);
  const { originX, originY, originZ } = innerPlane;
  for (let i = 0; i < outerBottomRail3.length; i += 3) {
    if (
      vec3PlaneSide(
        outerBottomRail3[i],
        outerBottomRail3[i + 1],
        outerBottomRail3[i + 2],
        originX,
        originY,
        originZ,
        nX,
        nY,
        nZ,
      ) !== PlaneSide.FRONT
    ) {
      isOuterBottonInside = true;
      break;
    }
  }

  if (!isOuterBottonInside) {
    return polyLine2OnPlane(innerPlane, innerBottomRail);
  }

  const offsetOutterPlane = planeMoveAlongNormal(planeCopy(outerPlane), -0.1);
  return polyLine2OnPlane(offsetOutterPlane, planeReProjectPolyLine2(innerBottomRail, innerPlane, offsetOutterPlane));
}

function isRakeRakeCorner(slope: number, neighborRail: PolyLine3 | undefined, isEnd: boolean): boolean {
  if (!neighborRail) {
    return false;
  }

  const neighborSlope = Math.abs(polyLine3EdgeSlopeInZ(neighborRail, isEnd ? 0 : polyLine3EdgeCount(neighborRail) - 1));

  return slope > DIST_EPSILON && neighborSlope > DIST_EPSILON;
}

function isRakeEaveCorner(slope: number, neighborRail: PolyLine3 | undefined, isEnd: boolean): boolean {
  if (!neighborRail) {
    return false;
  }

  const neighborSlope = Math.abs(polyLine3EdgeSlopeInZ(neighborRail, isEnd ? 0 : polyLine3EdgeCount(neighborRail) - 1));

  return (
    (slope < DIST_EPSILON && neighborSlope > DIST_EPSILON) || (slope > DIST_EPSILON && neighborSlope < DIST_EPSILON)
  );
}
